import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { Trash2, BookMarked, Bookmark, Heart, Download, Crown, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const EMPTY_IMG = "https://static.prod-images.emergentagent.com/jobs/d318c9c3-21c1-4ea6-9834-c444788acba2/images/102963b0360091a8fb42a057889451473766b48452933352dc43d164e573afaa.png";

const TABS = [
  { key: "owned", labelKey: "collection.owned", Icon: BookMarked },
  { key: "wishlist", labelKey: "collection.wishlist", Icon: Bookmark },
  { key: "favorite", labelKey: "collection.favorites", Icon: Heart },
];

export default function Collection() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("owned");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const isPremium = user && user.is_premium;

  const load = (currentTab) => {
    setLoading(true);
    api.get("/collection", { params: { status: currentTab } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  const loadPortfolio = () => {
    api.get("/collection/portfolio-value").then((r) => setPortfolio(r.data)).catch(() => setPortfolio(null));
  };

  useEffect(() => { load(tab); }, [tab]);
  useEffect(() => { loadPortfolio(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/collection/${id}`);
      setItems((s) => s.filter((i) => i.id !== id));
      loadPortfolio();
      toast.success(t("common.removed", "Removed"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-2">{t("collection.label")}</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter">{t("collection.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          {isPremium ? (
            <a href={`${BACKEND_URL}/api/collection/export.csv`} data-testid="csv-export-btn" className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> {t("collection.export_csv")}
            </a>
          ) : (
            <Link to="/pricing" data-testid="collection-upgrade-btn" className="btn-primary text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
              <Crown className="w-3.5 h-3.5" /> {t("collection.go_premium")}
            </Link>
          )}
        </div>
      </div>

      {/* Portfolio value summary */}
      {portfolio && portfolio.item_count > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="portfolio-summary">
          <div className="flat-card p-5 border-[#FACC15]/40">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">Portfolio value</span>
              <Wallet className="w-4 h-4 text-[#FACC15]" />
            </div>
            <div className="font-heading font-black text-3xl tracking-tighter neon-yellow" data-testid="portfolio-total-value">
              €{portfolio.total_value_eur.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="font-mono text-[10px] text-white/40 tracking-wider mt-1">
              {portfolio.priced_count}/{portfolio.item_count} cards priced
            </div>
          </div>
          <div className="flat-card p-5">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-2">Owned</div>
            <div className="font-heading font-black text-2xl tracking-tighter" data-testid="portfolio-owned-value">€{portfolio.owned_value_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          </div>
          <div className="flat-card p-5">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-2">Wishlist</div>
            <div className="font-heading font-black text-2xl tracking-tighter">€{portfolio.wishlist_value_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          </div>
          <div className="flat-card p-5">
            <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-2">Favorites</div>
            <div className="font-heading font-black text-2xl tracking-tighter">€{portfolio.favorite_value_eur.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-8 border-b border-white/10">
        {TABS.map(({ key, labelKey, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`collection-tab-${key}`}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-heading font-semibold uppercase tracking-wide border-b-2 transition ${
              tab === key ? "border-[#FACC15] text-[#FACC15]" : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Icon className="w-4 h-4" /> {t(labelKey)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-white/40 font-mono py-20 tracking-widest text-xs">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <img src={EMPTY_IMG} alt="Empty" className="w-48 mx-auto opacity-60" />
          <div className="font-heading font-bold text-xl tracking-tight mt-6">{t("collection.empty_title")}</div>
          <p className="text-white/50 text-sm mt-2 mb-6">{t("collection.empty_subtitle", { tab: t(`collection.${tab === "favorite" ? "favorites" : tab}`) })}</p>
          <Link to="/sets" data-testid="empty-browse-sets" className="btn-primary text-xs uppercase tracking-wider inline-flex">{t("collection.empty_cta")}</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {items.map((it) => {
            const snap = it.card_snapshot || {};
            return (
              <div key={it.id} className="card-tilt group relative" data-testid={`collection-item-${it.id}`}>
                <Link to={`/cards/${it.card_id}`}>
                  <div className="aspect-[2.5/3.5] overflow-hidden bg-black/40 border border-white/10">
                    {snap.image ? (
                      <img src={snap.image} alt={snap.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">No image</div>
                    )}
                  </div>
                </Link>
                <div className="px-1 pt-2 pb-3 flex items-start justify-between gap-1">
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm leading-tight tracking-tight truncate">{snap.name || it.card_id}</div>
                    <div className="font-mono text-[10px] text-white/40 mt-0.5 truncate">{snap.set_name}</div>
                  </div>
                  <button onClick={() => remove(it.id)} data-testid={`remove-${it.id}`} className="p-1 text-white/40 hover:text-[#FF007F] transition">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
